// Live game + review mode. Owns the effects: initial load, bot triggering,
// grade polling, scoring seeding, overlay data fetches, keyboard navigation.

import { lazy, Suspense, useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { Vertex } from '../../shared/types.ts';
import { EMPTY } from '../../shared/go/board.ts';
import { isResign, vertexToPt } from '../../shared/go/coords.ts';
import { checkMove } from '../../shared/go/rules.ts';
import { computeTerritory } from '../../shared/go/scoring.ts';
import { toSGF } from '../../shared/go/sgf.ts';
import { replayAt } from '../derive.ts';
import { analysisAvailability } from '../availability.ts';
import { navigate, setQuery } from '../router.ts';
import { ungradedMoveNumbers, useStore } from '../store/useStore.ts';
import { Goban, type BoardMode } from '../board/Goban.tsx';
import { ActionBar } from '../panels/ActionBar.tsx';
import { GameInfoStrip } from '../panels/GameInfoStrip.tsx';
import { MoveList } from '../panels/MoveList.tsx';
import { ResultModal } from '../panels/ResultModal.tsx';

const EMPTY_SET: ReadonlySet<Vertex> = new Set();
const loadCoachChat = () => import('../panels/CoachChat.tsx');
const CoachChat = lazy(() => loadCoachChat().then((module) => ({ default: module.CoachChat })));

export interface GameScreenProps {
  id: number;
  review: boolean;
  query: URLSearchParams;
}

export function GameScreen({ id, review, query }: GameScreenProps) {
  const [sheetExpanded, setSheetExpanded] = useState(() => query.get('tab') === 'coach');
  const detail = useStore((s) => s.detail);
  const record = useStore((s) => s.record);
  const cursor = useStore((s) => s.cursor);
  const setCursor = useStore((s) => s.setCursor);
  const botThinking = useStore((s) => s.botThinking);
  const lastError = useStore((s) => s.lastError);
  const announcement = useStore((s) => s.announcement);
  const fadingCaptures = useStore((s) => s.fadingCaptures);
  const deadStones = useStore((s) => s.deadStones);
  const forcedDame = useStore((s) => s.forcedDame);
  const analysis = useStore((s) => s.analysis);
  const analysisStatus = useStore((s) => s.analysisStatus);
  const scoreEstimate = useStore((s) => s.scoreEstimate);
  const scoreEstimateAt = useStore((s) => s.scoreEstimateAt);
  const pendingConfirm = useStore((s) => s.pendingConfirm);
  const sendChat = useStore((s) => s.sendChat);
  const health = useStore((s) => s.health);

  const tab = query.get('tab') === 'coach' ? 'coach' : 'moves';
  const overlay = query.get('overlay') ?? 'none';
  const modal = query.get('modal');
  const analysisAvailabilityState = analysisAvailability(health);

  const loadedForThisGame = detail !== null && detail.game.id === id;
  const status = loadedForThisGame ? detail.game.status : null;
  const moveCount = loadedForThisGame ? detail.moves.length : 0;
  const viewedTurn = cursor ?? moveCount;
  const breakdown = loadedForThisGame ? detail.scoreBreakdown : null;
  // Analysis turns index engine moves only (the server filters 'resign' and
  // clamps): mirror that clamp, or at the final position of a resigned game
  // the echoed turn never matches the requested one and no ghosts render.
  const engineMoveCount = loadedForThisGame
    ? detail.moves.reduce((n, m) => (isResign(m.vertex) ? n : n + 1), 0)
    : 0;
  const analysisTurn = Math.min(viewedTurn, engineMoveCount);

  // ----- effects -----

  useEffect(() => {
    void useStore.getState().loadGame(id, review);
  }, [id, review]);

  // Bot trigger: any response with botToMove:true eventually lands here.
  const botToMove = loadedForThisGame && detail.botToMove;
  useEffect(() => {
    if (!review && botToMove && status === 'active') {
      void useStore.getState().maybeBotMove();
    }
  }, [review, botToMove, status, moveCount]);

  // Poll while any played human move lacks a grade (pause when tab hidden).
  // Only live bot games get background grades: review mode, finished games and
  // hotseat games would poll forever with no grade ever arriving.
  useEffect(() => {
    if (review) return;
    const timer = setInterval(() => {
      if (document.hidden) return;
      const s = useStore.getState();
      if (!s.detail || s.playPending || s.botThinking || s.chatBusy) return;
      if (s.detail.game.status === 'finished') return; // nothing re-kicks grading
      if (s.health?.katago === 'down') return; // grades cannot arrive
      if (ungradedMoveNumbers(s.detail).length === 0) return;
      void s.refresh();
    }, 5000);
    return () => clearInterval(timer);
  }, [id, review]);

  // Entering the scoring phase: fetch ownership, seed dead groups + dame.
  useEffect(() => {
    if (review || status !== 'scoring') return;
    const attempt = () => {
      const s = useStore.getState();
      if (
        s.detail?.game.status === 'scoring' &&
        s.scoringSeededFor !== id &&
        !s.scoreLoading
      ) {
        void s.seedScoring();
      }
    };
    attempt();
    const timer = setInterval(attempt, 4000); // retries while KataGo warms
    return () => clearInterval(timer);
  }, [review, status, id]);

  // Suggestions overlay: keep analysis in sync with the viewed turn.
  useEffect(() => {
    if (overlay !== 'suggestions' || !loadedForThisGame) return;
    const s = useStore.getState();
    if (s.analysisStatus === 'loading') return;
    if (s.analysisStatus === 'warming') {
      const t = setTimeout(() => {
        const now = useStore.getState();
        if (now.analysisStatus !== 'loading') void now.fetchAnalysis(analysisTurn);
      }, 4000);
      return () => clearTimeout(t);
    }
    if (s.analysisFor !== analysisTurn) void s.fetchAnalysis(analysisTurn);
    return undefined;
  }, [overlay, analysisTurn, loadedForThisGame, analysisStatus]);

  // Territory overlay on a live board: refresh the ownership estimate.
  useEffect(() => {
    if (overlay !== 'territory' || !loadedForThisGame) return;
    if (status === 'scoring' || breakdown) return; // scoring seeds; breakdown is exact
    const s = useStore.getState();
    if (s.scoreLoading) return;
    if (s.analysisStatus === 'warming') {
      const t = setTimeout(() => void useStore.getState().fetchScoreEstimate(), 4000);
      return () => clearTimeout(t);
    }
    if (s.scoreEstimateAt !== moveCount || !s.scoreEstimate) void s.fetchScoreEstimate();
    return undefined;
  }, [overlay, loadedForThisGame, status, moveCount, breakdown, analysisStatus]);

  // The 'warming' status is only ever cleared by the overlay/scoring retry
  // loops above; once the last of them is dismissed, reset it so the
  // "KataGo is warming up" pill does not stick for the rest of the session.
  useEffect(() => {
    if (analysisStatus !== 'warming') return;
    const scoringDrives = !review && status === 'scoring';
    if (overlay === 'suggestions' || overlay === 'territory' || scoringDrives) return;
    useStore.setState({ analysisStatus: 'idle' });
  }, [analysisStatus, overlay, status, review]);

  // Keyboard: arrow keys scrub the move list; Escape collapses the mobile
  // sheet first, then cancels a staged 19x19 move.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (e.key === 'Escape') {
        // A modal owns Escape while it is open. Otherwise, collapse the study
        // sheet even when focus is inside the Coach composer.
        if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
        const s = useStore.getState();
        if (sheetExpanded) setSheetExpanded(false);
        else s.setPendingConfirm(null);
        return;
      }
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      const s = useStore.getState();
      if (!s.detail) return;
      const len = s.detail.moves.length;
      const cur = s.cursor ?? len;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        s.setCursor(Math.max(0, cur - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        s.setCursor(cur + 1 >= len ? null : cur + 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetExpanded]);

  // Error toast auto-dismiss.
  useEffect(() => {
    if (!lastError) return;
    const t = setTimeout(() => useStore.getState().dismissError(), 5000);
    return () => clearTimeout(t);
  }, [lastError]);

  // ----- derived board state -----

  const replayState = loadedForThisGame && record ? replayAt(record, viewedTurn) : null;

  const boardMode: BoardMode = (() => {
    if (!loadedForThisGame || review || cursor !== null) return 'inert';
    if (status === 'scoring') return 'scoring';
    if (status !== 'active') return 'inert';
    if (detail.game.mode === 'bot' && (detail.botToMove || botThinking)) return 'inert';
    return 'play';
  })();

  const scoringActive = !review && status === 'scoring' && cursor === null;
  const scoringTerritory = useMemo(() => {
    if (!scoringActive || !record) return null;
    return computeTerritory(replayAt(record).position, deadStones, forcedDame);
  }, [scoringActive, record, deadStones, forcedDame]);

  const showBreakdownOverlay =
    overlay === 'territory' && breakdown !== null && cursor === null;

  // Memoized on their inputs: a fresh Set identity every render would bust
  // the StonesLayer memo (and rebuild every stone subtree on 19x19 boards).
  const deadSet: ReadonlySet<Vertex> = useMemo(
    () =>
      scoringActive
        ? new Set(deadStones)
        : showBreakdownOverlay && breakdown
          ? new Set(breakdown.deadStones)
          : EMPTY_SET,
    [scoringActive, deadStones, showBreakdownOverlay, breakdown],
  );
  const dameSet: ReadonlySet<Vertex> = useMemo(
    () =>
      scoringActive
        ? new Set(scoringTerritory?.dame ?? [])
        : showBreakdownOverlay && breakdown
          ? new Set(breakdown.damePoints)
          : EMPTY_SET,
    [scoringActive, scoringTerritory, showBreakdownOverlay, breakdown],
  );
  const territory = scoringActive
    ? (scoringTerritory?.territory ?? null)
    : showBreakdownOverlay
      ? breakdown.territoryPoints
      : null;

  const estimateFresh =
    scoreEstimate !== null && scoreEstimateAt === moveCount && cursor === null;
  const ownership =
    overlay === 'territory' && estimateFresh && !showBreakdownOverlay
      ? scoreEstimate.ownership
      : null;

  const suggestions =
    overlay === 'suggestions' && analysis && analysis.turn === analysisTurn
      ? analysis.moves
      : null;

  const lastMove =
    loadedForThisGame && viewedTurn > 0 ? (detail.moves[viewedTurn - 1]?.vertex ?? null) : null;

  // ----- interactions -----

  const handlePoint = (vertex: Vertex) => {
    const s = useStore.getState();
    if (!s.detail || !s.record || review || s.cursor !== null) return;
    const st = s.detail.game.status;
    const size = s.detail.game.boardSize;
    if (st === 'scoring') {
      const pos = replayAt(s.record).position;
      const pt = vertexToPt(vertex, size);
      if (pos.stones[pt] === EMPTY) s.toggleDame(vertex);
      else s.toggleDead(vertex);
      return;
    }
    if (st !== 'active') return;
    if (s.detail.game.mode === 'bot' && s.detail.botToMove) return;
    if (size === 19 && s.confirm19) {
      if (s.pendingConfirm === vertex) {
        void s.play(vertex);
      } else {
        const pos = replayAt(s.record).position;
        if (checkMove(pos, s.detail.toPlay, vertexToPt(vertex, size)) === null) {
          s.setPendingConfirm(vertex);
        }
      }
      return;
    }
    void s.play(vertex);
  };

  const downloadSgf = () => {
    if (!record || !loadedForThisGame) return;
    const name = (color: 'B' | 'W') =>
      detail.game.mode === 'hotseat'
        ? color === 'B'
          ? 'Black'
          : 'White'
        : detail.game.humanColor === color
          ? 'Human'
          : `GNU Go L${detail.game.botLevel ?? '?'}`;
    const sgf = toSGF(record, {
      result: detail.game.result,
      playerBlack: name('B'),
      playerWhite: name('W'),
      date: detail.game.createdAt.slice(0, 10),
    });
    const blob = new Blob([sgf], { type: 'application/x-go-sgf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `learn-baduk-${id}.sgf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const explainScore = () => {
    setQuery({ modal: null, tab: 'coach' });
    setSheetExpanded(true);
    void sendChat('Walk me through this score.');
  };

  const selectTab = (next: 'moves' | 'coach') => {
    setQuery({ tab: next });
    setSheetExpanded(true);
  };

  const handleTabKey = (event: ReactKeyboardEvent, current: 'moves' | 'coach') => {
    const next =
      event.key === 'Home'
        ? 'moves'
        : event.key === 'End'
          ? 'coach'
          : event.key === 'ArrowLeft' || event.key === 'ArrowRight'
            ? current === 'moves' ? 'coach' : 'moves'
            : null;
    if (!next) return;
    event.preventDefault();
    selectTab(next);
    document.getElementById(`${next}-tab`)?.focus();
  };

  // ----- render -----

  if (!loadedForThisGame || !replayState) {
    return (
      <main className="game-screen">
        <p className="loading-note">{lastError ?? 'Setting out the stones…'}</p>
        {lastError && (
          <p className="loading-note">
            <a href="#/history">← Back to past games</a>
          </p>
        )}
      </main>
    );
  }

  const toPlayForBoard = cursor === null ? detail.toPlay : replayState.toPlay;
  const mobileStatus =
    review || cursor !== null
      ? `Reviewing move ${viewedTurn} of ${moveCount}`
      : status === 'scoring'
        ? 'Scoring the board'
        : status === 'finished'
          ? `Game finished · ${detail.game.result ?? 'result ready'}`
          : detail.game.mode === 'hotseat'
            ? `${toPlayForBoard === 'B' ? 'Black' : 'White'} to play`
            : detail.game.humanColor === toPlayForBoard
              ? 'Your turn'
              : 'GNU Go to play';

  return (
    <main className="game-screen">
      <div className="game-layout">
        <section className="board-column">
          <div className="mobile-turn-status" role="status">
            <span className={`stone-icon ${toPlayForBoard === 'B' ? 'stone-icon-b' : 'stone-icon-w'}`} aria-hidden="true" />
            <span>{mobileStatus}</span>
          </div>
          <div className="board-wrap">
            <Goban
              size={detail.game.boardSize}
              position={replayState.position}
              toPlay={toPlayForBoard}
              mode={boardMode}
              lastMove={lastMove}
              deadStones={deadSet}
              dameMarks={dameSet}
              territory={territory}
              ownership={ownership}
              suggestions={suggestions}
              fadingCaptures={cursor === null ? fadingCaptures : []}
              pendingConfirm={pendingConfirm}
              onPointClick={boardMode === 'inert' ? undefined : handlePoint}
            />
            {botThinking && (
              <div className="board-pill thinking-pill" role="status">
                <span className="pulse-dot" aria-hidden="true" /> GNU Go is thinking…
              </div>
            )}
            {pendingConfirm && !botThinking && (
              <div className="board-pill confirm-pill">
                Click again to confirm <strong className="mono">{pendingConfirm}</strong>
                <button
                  type="button"
                  className="pill-cancel"
                  onClick={() => useStore.getState().setPendingConfirm(null)}
                >
                  cancel
                </button>
              </div>
            )}
            {overlay === 'suggestions' && analysisStatus === 'loading' && (
              <div className="board-pill">Analyzing…</div>
            )}
            {analysisStatus === 'warming' && (
              <div className="board-pill">KataGo is warming up — hold on…</div>
            )}
          </div>

          {review ? (
            <div className="review-bar">
              <div className="review-controls">
                <button type="button" className="btn btn-quiet" onClick={() => setCursor(0)} aria-label="Jump to start">
                  ⏮
                </button>
                <button
                  type="button"
                  className="btn btn-quiet"
                  onClick={() => setCursor(Math.max(0, viewedTurn - 1))}
                  aria-label="Previous move"
                >
                  ◀
                </button>
                <input
                  type="range"
                  className="review-slider"
                  min={0}
                  max={moveCount}
                  step={1}
                  value={viewedTurn}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setCursor(v >= moveCount ? null : v);
                  }}
                  aria-label="Move cursor"
                />
                <button
                  type="button"
                  className="btn btn-quiet"
                  onClick={() => setCursor(viewedTurn + 1 >= moveCount ? null : viewedTurn + 1)}
                  aria-label="Next move"
                >
                  ▶
                </button>
                <button type="button" className="btn btn-quiet" onClick={() => setCursor(null)} aria-label="Jump to latest">
                  ⏭
                </button>
                <span className="mono review-counter">
                  {viewedTurn} / {moveCount}
                </span>
              </div>
              <div className="action-group">
                <button
                  type="button"
                  className={`btn btn-toggle ${overlay === 'territory' ? 'is-on' : ''}`}
                  aria-pressed={overlay === 'territory'}
                  onClick={() => setQuery({ overlay: overlay === 'territory' ? null : 'territory' })}
                >
                  Territory
                </button>
                <button
                  type="button"
                  className={`btn btn-toggle ${overlay === 'suggestions' ? 'is-on' : ''}`}
                  aria-pressed={overlay === 'suggestions'}
                  disabled={health?.katago !== 'ready'}
                  onClick={() =>
                    setQuery({ overlay: overlay === 'suggestions' ? null : 'suggestions' })
                  }
                >
                  Suggestions
                </button>
                <button type="button" className="btn" onClick={downloadSgf}>
                  SGF
                </button>
                <button type="button" className="btn" onClick={() => navigate('/history')}>
                  History
                </button>
              </div>
              {analysisAvailabilityState !== 'ready' && (
                <p className="engine-note" role="status">
                  Analysis tools are {analysisAvailabilityState === 'checking' ? 'being checked' : analysisAvailabilityState === 'warming' ? 'warming up' : 'currently unavailable'}.
                </p>
              )}
            </div>
          ) : (
            <ActionBar overlay={overlay} />
          )}
        </section>

        <aside className={`side-panel ${sheetExpanded ? 'is-expanded' : 'is-collapsed'}`} aria-label="Game details">
          <GameInfoStrip
            viewedTurn={viewedTurn}
            captures={cursor === null ? detail.captures : replayState.captures}
            toPlay={toPlayForBoard}
          />
          <div className="tabs" role="tablist" aria-label="Game details">
            <button
              id="moves-tab"
              type="button"
              role="tab"
              aria-selected={tab === 'moves'}
              aria-controls="side-tabpanel"
              tabIndex={tab === 'moves' ? 0 : -1}
              className={`tab ${tab === 'moves' ? 'is-active' : ''}`}
              onClick={() => selectTab('moves')}
              onKeyDown={(event) => handleTabKey(event, 'moves')}
            >
              <span aria-hidden="true">☷</span> Moves
              <span className="tab-count mono">{moveCount}</span>
            </button>
            <button
              id="coach-tab"
              type="button"
              role="tab"
              aria-selected={tab === 'coach'}
              aria-controls="side-tabpanel"
              tabIndex={tab === 'coach' ? 0 : -1}
              className={`tab ${tab === 'coach' ? 'is-active' : ''}`}
              onMouseEnter={loadCoachChat}
              onFocus={loadCoachChat}
              onClick={() => selectTab('coach')}
              onKeyDown={(event) => handleTabKey(event, 'coach')}
            >
              <span aria-hidden="true">✦</span> Coach
            </button>
            <button
              type="button"
              className="sheet-collapse"
              aria-label="Collapse game details"
              aria-expanded={sheetExpanded}
              onClick={() => setSheetExpanded(false)}
            >
              <span aria-hidden="true">⌄</span>
            </button>
          </div>
          <div
            id="side-tabpanel"
            className="tab-body"
            role="tabpanel"
            aria-labelledby={`${tab}-tab`}
            tabIndex={0}
          >
            {tab === 'coach' ? (
              <Suspense fallback={<p className="panel-loading" role="status">Opening the study notes…</p>}>
                <CoachChat />
              </Suspense>
            ) : (
              <MoveList />
            )}
          </div>
        </aside>
      </div>

      {modal === 'result' && breakdown && (
        <ResultModal
          breakdown={breakdown}
          onClose={() => setQuery({ modal: null })}
          onExplain={explainScore}
          onReview={() => navigate(`/review/${id}`)}
          onNewGame={() => navigate('/')}
        />
      )}

      <div className="sr-only" aria-live="polite">
        {announcement}
      </div>

      {lastError && (
        <div className="toast" role="alert">
          {lastError}
          <button
            type="button"
            className="toast-close"
            aria-label="Dismiss"
            onClick={() => useStore.getState().dismissError()}
          >
            ×
          </button>
        </div>
      )}
    </main>
  );
}
